import React, {useState} from 'react';
import styled from 'styled-components';
import {graphql, ForecastQueryRenderer} from '../../../graphql/forecastRelay';
import {DashboardPeriodPicker} from '../components/DashboardPeriodPicker';
import {DashboardVisualizationRow} from '../components/DashboardVisualizationRow';
import {DashboardGroupedTickets} from '../components/DashboardGroupedTickets';
import {EmptyState} from '../../tickets/components/EmptyState';
import {DATE_PRESETS} from '../../tickets/constants/datePresets';
import type {GroupableTicket} from '../types/groupableTicket';
import type {DashboardRouteQuery as DashboardRouteQueryType} from './__generated__/DashboardRouteQuery.graphql';

const DashboardRouteQueryDoc = graphql`
	query DashboardRouteQuery($input: TicketDashboardAnalyticsInput!) {
		viewer {
			actualPersonId
			ticketDashboardAnalytics(input: $input) {
				gauge {
					achieved
					breached
					total
					ratePct
					targetPct
				}
				trend {
					date
					created
					resolved
					breached
				}
			}
		}
	}
`;

const Container = styled.div`
	margin: 0 auto;
	max-width: 80rem;
	padding: 24px;
`;

const Title = styled.h1`
	${p => p.theme.sys.font?.heading?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	margin: 0 0 24px;
`;

const Loading = styled.div`
	padding: 16px;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;

/**
 * The dashboard's ticket rows come from the bulk ticket query (FOR-70), which is
 * not built yet, and are presented by the ticket data table (FOR-73). Grouping is
 * a layout over those rows, so it ships with nothing to group and its section
 * renders its own empty state until they arrive. Named as a stable constant rather
 * than a literal so the grouped section's `useMemo` isn't invalidated every render.
 */
const DASHBOARD_TICKETS: readonly GroupableTicket[] = [];

function getDefaultRange() {
	const preset = DATE_PRESETS.find(p => p.id === 'last_30_days');
	return preset ? preset.getRange() : {from: '', to: ''};
}

export function DashboardRoute() {
	const defaultRange = getDefaultRange();
	const [from, setFrom] = useState(defaultRange.from);
	const [to, setTo] = useState(defaultRange.to);

	return (
		<Container>
			<Title>Dashboard</Title>
			{/* Provisional: the visualisation row needs a period to query, so FOR-72 ships the
			    smallest control that supplies one. FOR-71 replaces this with the real dashboard
			    toolbar (date field, range presets, date pills) — delete this component then
			    rather than growing it here. */}
			<DashboardPeriodPicker from={from} to={to} onChangeFrom={setFrom} onChangeTo={setTo} />
			<ForecastQueryRenderer
				query={DashboardRouteQueryDoc}
				variables={{input: {from, to}}}
				customLoader={() => <Loading>Loading…</Loading>}
				redirectIfNotLoggedIn
				render={(relayProps: DashboardRouteQueryType['response'] | null) => {
					// `ticketDashboardAnalytics` is nullable — the account may have no ticketing
					// data to aggregate. Say so, rather than leaving the page blank below the
					// heading. (The per-card "no tickets resolved" / "no data" states live in the
					// gauge and chart; this covers having no analytics payload at all.)
					if (!relayProps?.viewer?.ticketDashboardAnalytics) {
						return (
							<EmptyState variant="inset" data-testid="dashboard-no-analytics">
								No ticket analytics available for this period.
							</EmptyState>
						);
					}
					const {gauge, trend} = relayProps.viewer.ticketDashboardAnalytics;
					return <DashboardVisualizationRow gauge={gauge} trend={[...trend]} />;
				}}
			/>
			{/* Outside the query boundary: grouping re-lays-out rows client-side and issues
			    no query of its own (PRD §13), so it neither waits on the analytics fetch nor
			    disappears when that fetch returns nothing. */}
			<DashboardGroupedTickets tickets={DASHBOARD_TICKETS} />
		</Container>
	);
}
